/**
 * Fechas del calendario alineadas con la zona del negocio (horarios).
 * Si no hay zona válida, se usa la fecha local del dispositivo.
 */

/**
 * Convierte el string de horarios (ej. GMT-06:00, America/Mexico_City) a IANA.
 * @param {string|null|undefined} raw
 * @returns {string|null}
 */
export function resolveCalendarTimeZone(raw) {
  if (!raw || typeof raw !== 'string') return null
  const s = raw.trim()
  if (s === 'UTC') return 'UTC'

  if (s.includes('/')) {
    try {
      Intl.DateTimeFormat('en-US', { timeZone: s })
      return s
    } catch {
      return null
    }
  }

  const m = s.match(/^GMT\s*([+-])(\d{1,2})(?::(\d{2}))?$/i)
  if (!m) return null
  const sign = m[1]
  const hours = parseInt(m[2], 10)
  const mins = m[3] ? parseInt(m[3], 10) : 0
  if (mins !== 0) return null
  // GMT-6 (oeste de Greenwich) = UTC-6 → en base tz "Etc/GMT+6" (signo invertido)
  const etcSign = sign === '-' ? '+' : '-'
  const iana = `Etc/GMT${etcSign}${hours}`
  try {
    Intl.DateTimeFormat('en-US', { timeZone: iana })
    return iana
  } catch {
    return null
  }
}

/**
 * Clave YYYY-MM-DD del instante `date` en la zona indicada (o local si timeZoneIana es null).
 * @param {Date|string|number} date
 * @param {string|null} timeZoneIana
 */
export function calendarDateKey(date, timeZoneIana) {
  const d = date instanceof Date ? date : new Date(date)
  if (Number.isNaN(d.getTime())) return ''
  if (!timeZoneIana) {
    const y = d.getFullYear()
    const mo = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${y}-${mo}-${day}`
  }
  try {
    const fmt = new Intl.DateTimeFormat('en-CA', {
      timeZone: timeZoneIana,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    })
    const parts = fmt.formatToParts(d)
    const y = parts.find((p) => p.type === 'year')?.value
    const mo = parts.find((p) => p.type === 'month')?.value
    const da = parts.find((p) => p.type === 'day')?.value
    if (y && mo && da) return `${y}-${mo}-${da}`
  } catch {
    /* fallthrough */
  }
  return calendarDateKey(d, null)
}

export function isPastCalendarDay(date, timeZoneIana) {
  const key = calendarDateKey(date, timeZoneIana)
  const todayKey = calendarDateKey(new Date(), timeZoneIana)
  if (!key || !todayKey) return false
  return key < todayKey
}

export function isSameCalendarDay(a, b, timeZoneIana) {
  return calendarDateKey(a, timeZoneIana) === calendarDateKey(b, timeZoneIana)
}

export function isTodayCalendarDay(date, timeZoneIana) {
  return isSameCalendarDay(date, new Date(), timeZoneIana)
}

/**
 * Minutos desde medianoche del instante `date` en la zona indicada (o local si no hay IANA).
 * @param {Date} date
 * @param {string|null} timeZoneIana
 */
export function getClockMinutesInTimeZone(date, timeZoneIana) {
  const d = date instanceof Date ? date : new Date(date)
  if (Number.isNaN(d.getTime())) return 0
  if (!timeZoneIana) {
    return d.getHours() * 60 + d.getMinutes()
  }
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: timeZoneIana,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).formatToParts(d)
    const hour = parseInt(parts.find((p) => p.type === 'hour')?.value ?? '0', 10)
    const minute = parseInt(parts.find((p) => p.type === 'minute')?.value ?? '0', 10)
    return (Number.isFinite(hour) ? hour : 0) * 60 + (Number.isFinite(minute) ? minute : 0)
  } catch {
    return d.getHours() * 60 + d.getMinutes()
  }
}

/**
 * Vista semana: celda de hora entera [hourStart, hourStart+1) ya pasó (hoy en zona del negocio).
 * Días anteriores a hoy: siempre true (la columna entera es pasada).
 */
export function isWeekHourSlotPast(dayDate, hourStart, timeZoneIana) {
  if (!dayDate || !(dayDate instanceof Date) || Number.isNaN(dayDate.getTime())) return false
  if (isPastCalendarDay(dayDate, timeZoneIana)) return true
  if (!isTodayCalendarDay(dayDate, timeZoneIana)) return false
  const nowM = getClockMinutesInTimeZone(new Date(), timeZoneIana)
  const slotEndM = (hourStart + 1) * 60
  return nowM >= slotEndM
}

/**
 * Franja horizontal de días: estilo deshabilitado si el día ya pasó,
 * no hay franjas (día no configurado), o (si es hoy) ya pasó la última franja.
 * @param {Date} date
 * @param {string|null} timeZoneIana
 * @param {string[]} availableHours - HH:mm desde getAvailableHoursForDay (vacío = sin agenda)
 */
export function isCalendarStripDayDisabled(date, timeZoneIana, availableHours) {
  if (!date || !(date instanceof Date) || Number.isNaN(date.getTime())) return true
  if (isPastCalendarDay(date, timeZoneIana)) return true
  if (!Array.isArray(availableHours) || availableHours.length === 0) return true
  const now = new Date()
  const todayKey = calendarDateKey(now, timeZoneIana)
  const dateKey = calendarDateKey(date, timeZoneIana)
  if (dateKey !== todayKey) return false
  const nowM = getClockMinutesInTimeZone(now, timeZoneIana)
  const slotMins = availableHours.map((h) => {
    const [hh, mm] = String(h).split(':').map(Number)
    return (Number.isFinite(hh) ? hh : 0) * 60 + (Number.isFinite(mm) ? mm : 0)
  })
  const maxStart = Math.max(...slotMins)
  return maxStart < nowM
}

/**
 * Primer día “válido” para abrir el calendario: hoy si tiene franjas disponibles,
 * si no el siguiente día con agenda (misma lógica que la franja de días).
 * @param {Date} anchorDate — normalmente “ahora”
 * @param {string|null} timeZoneIana
 * @param {(d: Date) => string[]} getAvailableHoursForDay
 */
export function findDefaultCalendarViewDate(anchorDate, timeZoneIana, getAvailableHoursForDay) {
  const start = new Date(anchorDate)
  start.setHours(0, 0, 0, 0)
  for (let i = 0; i < 370; i++) {
    const d = new Date(start)
    d.setDate(start.getDate() + i)
    const hours = getAvailableHoursForDay(d)
    if (!isCalendarStripDayDisabled(d, timeZoneIana, hours)) {
      return d
    }
  }
  return new Date(start)
}

/**
 * Primer día **después** de `anchorDate` con al menos una franja en horario (config del negocio).
 * @param {Date} anchorDate
 * @param {(d: Date) => string[]} getAvailableHoursForDay
 * @returns {Date|null}
 */
export function findNextDayWithAvailableHours(anchorDate, getAvailableHoursForDay) {
  const start = new Date(anchorDate)
  start.setHours(0, 0, 0, 0)
  for (let i = 1; i < 370; i++) {
    const d = new Date(start)
    d.setDate(start.getDate() + i)
    const hours = getAvailableHoursForDay(d)
    if (Array.isArray(hours) && hours.length > 0) return d
  }
  return null
}

/**
 * Comparación de fecha de cita YYYY-MM-DD con "hoy" en zona del negocio.
 */
export function isAppointmentDatePast(dateStr, timeZoneIana) {
  if (!dateStr || typeof dateStr !== 'string') return false
  const todayKey = calendarDateKey(new Date(), timeZoneIana)
  return dateStr < todayKey
}

/**
 * Instante local de una cita a partir de YYYY-MM-DD y HH:mm (o HH:mm:ss).
 * @param {string} dateStr
 * @param {string|undefined|null} timeStr
 * @returns {Date}
 */
export function parseAppointmentDateTime(dateStr, timeStr) {
  if (!dateStr || typeof dateStr !== 'string') return new Date(NaN)
  const clean = dateStr.trim().slice(0, 10)
  const dm = /^(\d{4})-(\d{2})-(\d{2})$/.exec(clean)
  if (!dm) return new Date(NaN)
  const y = Number(dm[1])
  const mo = Number(dm[2])
  const d = Number(dm[3])
  if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) return new Date(NaN)

  let hours = 0
  let minutes = 0
  let seconds = 0
  if (timeStr && typeof timeStr === 'string') {
    const t = timeStr.trim()
    const parts = t.split(':')
    const h = parseInt(parts[0], 10)
    const min = parts.length > 1 ? parseInt(parts[1], 10) : 0
    const sec = parts.length > 2 ? parseInt(parts[2], 10) : 0
    if (Number.isFinite(h)) hours = h
    if (Number.isFinite(min)) minutes = min
    if (Number.isFinite(sec)) seconds = sec
  }
  const out = new Date(y, mo - 1, d, hours, minutes, seconds)
  return Number.isNaN(out.getTime()) ? new Date(NaN) : out
}
